import { Col, Pagination, Row, Spinner } from 'react-bootstrap';
import classNames from 'classnames';
import usePagination from '../../hooks/usePagination';

// The pager for a table whose rows come one page at a time.
//
// `AdvanceTableFooter` counts what the browser is holding —
// `getPrePaginationRowModel().rows.length` — which on a server-paged table is
// the twenty-five rows of the current page. It would read "1 to 25 items of 25"
// on page four of nine hundred. The totals here come from the server instead.
//
// The page number is deliberately 1-based on screen and 0-based in the table's
// own state, the same split tanstack uses, so the caller passes whatever its API
// already speaks.

const AdvanceTableServerFooter = ({
  className,
  page,          // 1-based, what the API was asked for
  pages,         // total pages, from the server
  total,         // total rows, from the server
  pageSize,
  onPage,
  // True while the NEXT page is in flight. Shown rather than swapping the table
  // for a spinner: with `keepPreviousData` the previous page stays on screen,
  // and blanking it would read as the data having gone away.
  isFetching
}) => {
  const totalPages = Math.max(1, Number(pages) || 1);
  const current = Math.min(Math.max(1, Number(page) || 1), totalPages);

  const { hasNextEllipsis, hasPrevEllipsis, visiblePaginationItems } =
    usePagination({
      currentPageNo: current,
      totalPage: totalPages,
      maxPaginationButtonCount: 5
    });

  const first = total === 0 ? 0 : (current - 1) * pageSize + 1;
  const last = Math.min(current * pageSize, Number(total) || 0);

  return (
    <Row className={classNames(className, 'align-items-center py-1')}>
      <Col className="d-flex align-items-center fs-9">
        <p className="mb-0 d-none d-sm-block me-2 fw-semi-bold text-900">
          {first.toLocaleString()} to {last.toLocaleString()}
          <span className="text-600"> of </span>
          {(Number(total) || 0).toLocaleString()}
        </p>
        {isFetching && <Spinner animation="border" size="sm" variant="primary" />}
      </Col>

      <Col xs="auto">
        <Pagination className="mb-0 justify-content-center">
          <Pagination.Prev disabled={current <= 1} onClick={() => onPage(current - 1)}>
            Newer
          </Pagination.Prev>

          {visiblePaginationItems.map(pageNo => (
            <div key={pageNo}>
              {hasPrevEllipsis && pageNo === visiblePaginationItems[0] && (
                <>
                  <Pagination.Item onClick={() => onPage(1)}>1</Pagination.Item>
                  <Pagination.Ellipsis disabled />
                </>
              )}

              <Pagination.Item active={current === pageNo} onClick={() => onPage(pageNo)}>
                {pageNo}
              </Pagination.Item>

              {hasNextEllipsis &&
                pageNo === visiblePaginationItems[visiblePaginationItems.length - 1] && (
                  <>
                    <Pagination.Ellipsis disabled />
                    <Pagination.Item onClick={() => onPage(totalPages)}>
                      {totalPages}
                    </Pagination.Item>
                  </>
                )}
            </div>
          ))}

          <Pagination.Next
            disabled={current >= totalPages}
            onClick={() => onPage(current + 1)}
          >
            Older
          </Pagination.Next>
        </Pagination>
      </Col>
    </Row>
  );
};

export default AdvanceTableServerFooter;
